import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, X, ArrowRight, Volume2, Eye } from 'lucide-react';
import { supabase } from '../utils/supabase';
import { playSuccessSound, playWrongSound } from '../utils/audio';
import { useTranslation } from '../utils/translations';

interface WordTestPageProps {
  words: string[];
  nativeLanguage?: string;
  onComplete: (score: number) => void;
  trackScore?: boolean;
}

interface TestQuestion {
  sentence: string;
  translation: string;
  persian_translation?: string;
  persian_translation?: string;
  word: string;
  options: string[];
  correctAnswer: string;
}

export function WordTestPage({ words, nativeLanguage = 'English', onComplete, trackScore = true }: WordTestPageProps) {
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation(nativeLanguage);
  const [questions, setQuestions] = useState<TestQuestion[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
  const [score, setScore] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showTranslation, setShowTranslation] = useState(false);
  const navigate = useNavigate();
  const [showWordQuiz, setShowWordQuiz] = useState(false);
  const [skippedWords, setSkippedWords] = useState<string[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const loadWordsRef = useRef<(() => Promise<void>) | undefined>();
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    
    // Check if user is admin
    const checkAdmin = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('is_admin')
          .eq('user_id', user.id)
          .single();
        setIsAdmin(profile?.is_admin || false);
      }
    };

    checkAdmin();

    const loadQuestions = async () => {
      try {
        setLoading(true);
        // For review quiz (trackScore=true), take up to 10 words
        // For lesson quiz (trackScore=false), use only the provided words
        const testWords = trackScore ? words.slice(0, 10) : words;

        // Get word bank entries for the unknown words
        const { data: wordBankEntries, error: wordBankError } = await supabase
          .from('word_bank')
          .select(`
            dutch,
            translation,
            persian_translation,
            pronunciation,
            example1,
            example1_translation,
            example1_persian_translation,
            example2,
            example2_translation,
            example2_persian_translation,
            example3,
            example3_translation,
            example3_persian_translation
          `)
          .in('dutch', testWords.map(w => w.toLowerCase().trim()));

        if (wordBankError) throw wordBankError;

        // Get additional random words for options
        const { data: randomWords } = await supabase
          .from('word_bank')
          .select('dutch')
          .not('dutch', 'in', `(${testWords.map(w => `'${w}'`).join(',')})`)
          .limit(50);

        if (!wordBankEntries || !randomWords) return;

        if (!mountedRef.current) return;

        // Create questions from word bank entries
        const testQuestions: TestQuestion[] = [];
        for (const entry of wordBankEntries) {
          // Get example sentence with translation
          const examples = [];
          if (entry.example1 && entry.example1_translation) {
            examples.push({ 
              dutch: entry.example1, 
              english: entry.example1_translation,
              persian: entry.example1_persian_translation 
            });
          }
          if (entry.example2 && entry.example2_translation) {
            examples.push({ 
              dutch: entry.example2, 
              english: entry.example2_translation,
              persian: entry.example2_persian_translation 
            });
          }
          if (entry.example3 && entry.example3_translation) {
            examples.push({ 
              dutch: entry.example3, 
              english: entry.example3_translation,
              persian: entry.example3_persian_translation 
            });
          }
          
          if (examples.length > 0) {
            // Randomly select an example
            const example = examples[Math.floor(Math.random() * examples.length)];

            // Create masked sentence
            const maskedSentence = example.dutch.replace(
              new RegExp(`\\b${entry.dutch}\\b`, 'gi'),
              '_____'
            );

            // Get appropriate translation based on native language
            const translation = nativeLanguage === 'Persian' && example.persian
              ? example.persian
              : example.english;

            // Get random incorrect options from word bank
            const incorrectOptions = randomWords
              .filter(w => w.dutch.toLowerCase() !== entry.dutch.toLowerCase())
              .sort(() => 0.5 - Math.random())
              .slice(0, 4)
              .map(w => w.dutch) || [];

            // Add the correct answer and shuffle
            const options = [...incorrectOptions, entry.dutch]
              .sort(() => 0.5 - Math.random());

            // Ensure we have exactly 5 options
            if (options.length > 5) {
              options.length = 5;
            }

            testQuestions.push({
              sentence: maskedSentence,
              translation,
              persian_translation: example.persian,
              word: entry.dutch,
              options,
              correctAnswer: entry.dutch
            });
          }
        }

        // Only add additional questions for review quiz
        if (trackScore && testQuestions.length < 10) {
          const remainingCount = 10 - testQuestions.length;
          // Add more questions from word bank if needed
          const { data: additionalWords } = await supabase
            .from('word_bank')
            .select('*')
            .not('dutch', 'in', `(${testWords.map(w => `'${w}'`).join(',')})`)
            .limit(remainingCount);

          if (additionalWords) {
            for (const word of additionalWords) {
              if (word.example1 && word.example1_translation) {
                const maskedSentence = word.example1.replace(
                  new RegExp(`\\b${word.dutch}\\b`, 'gi'),
                  '_____'
                );

                const incorrectOptions = randomWords
                  .filter(w => w.dutch.toLowerCase() !== word.dutch.toLowerCase())
                  .sort(() => 0.5 - Math.random())
                  .slice(0, 4)
                  .map(w => w.dutch) || [];

                const options = [...incorrectOptions, word.dutch]
                  .sort(() => 0.5 - Math.random());

                testQuestions.push({
                  sentence: maskedSentence,
                  translation: word.example1_translation,
                  persian_translation: word.example1_persian_translation,
                  word: word.dutch,
                  options,
                  correctAnswer: word.dutch
                });

                if (testQuestions.length >= 10) break;
              }
            }
          }
        }

        // Sort questions to match original word order for lesson quiz
        if (!trackScore) {
          const wordOrder = new Map(words.map((word, index) => [word.toLowerCase(), index]));
          testQuestions.sort((a, b) => {
            const aIndex = wordOrder.get(a.word.toLowerCase()) ?? 0;
            const bIndex = wordOrder.get(b.word.toLowerCase()) ?? 0;
            return aIndex - bIndex;
          });
        }

        if (mountedRef.current) {
          setQuestions(testQuestions);
        }
      } catch (error) {
        console.error('Error loading test questions:', error);
        if (mountedRef.current) {
          setWordData(null);
          setCurrentWordIndex(prev => prev + 1);
        }
      } finally {
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    };

    // Store the loadWords function in the ref
    loadWordsRef.current = loadQuestions;

    if (words.length) {
      loadQuestions();
    }
    
    return () => {
      mountedRef.current = false;
    };
  }, [words]);

  const playAudio = (text: string) => {
    if (isPlaying) {
      window.speechSynthesis.cancel();
      setIsPlaying(false);
      return;
    }

    setIsPlaying(true);
    // Get the full sentence with the correct word inserted
    const fullSentence = currentQuestion.sentence.replace('_____', currentQuestion.correctAnswer);
    const utterance = new SpeechSynthesisUtterance(fullSentence);
    utterance.lang = 'nl-NL';
    utterance.rate = 0.8;

    utterance.onend = () => {
      setIsPlaying(false);
    };

    utterance.onerror = (event) => {
      console.error('Speech synthesis error:', event);
      setIsPlaying(false);
    };

    window.speechSynthesis.cancel(); // Cancel any ongoing speech
    window.speechSynthesis.speak(utterance);
  };

  const handleAnswerSelect = async (answer: string) => {
    if (selectedAnswer !== null) return;

    let newScore = score;
    try {
    const correct = answer.toLowerCase().replace(/[.,!?؟;:()'"\-]/g, '').trim() === 
        questions[currentQuestionIndex].correctAnswer.toLowerCase().replace(/[.,!?؟]/g, '').trim();
      setIsCorrect(correct);

      if (correct) {
        newScore = score + 1;
        setScore(newScore);
        await playSuccessSound();
      } else {
        await playWrongSound();
      }
      
      if (trackScore) {
        // For review quiz, wait and proceed automatically
        setTimeout(() => {
          if (currentQuestionIndex === questions.length - 1) {
            // Pass final score including this answer
            if (newScore >= 9) {
              onComplete(newScore);
            } else {
              // Reset quiz for retry
              setCurrentQuestionIndex(0);
              setSelectedAnswer(null);
              setIsCorrect(null);
              setScore(0);
              // Shuffle questions for variety
              setQuestions(questions => [...questions].sort(() => Math.random() - 0.5));
            }
          } else {
            setCurrentQuestionIndex(prev => prev + 1);
            setSelectedAnswer(null);
            setIsCorrect(null);
          }
        }, 1500);
      } else {
        // For lesson quiz, proceed after a short delay regardless of answer
        setTimeout(() => {
          if (currentQuestionIndex === questions.length - 1) {
            onComplete(0); // Score doesn't matter for lesson quiz
          } else {
            setCurrentQuestionIndex(prev => prev + 1);
            setSelectedAnswer(null);
            setIsCorrect(null);
          }
        }, 800);
      }
    } catch (error) {
      console.error('Error handling answer:', error);
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <p className="text-gray-600">Loading test questions...</p>
        </div>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <p className="text-gray-600">No practice questions available.</p>
          <button
            onClick={() => onComplete(score)}
            className="mt-4 flex items-center px-6 py-3 rounded-full bg-green-500 hover:bg-green-600 text-white transition-colors"
          >
            Continue
            <ArrowRight className="h-5 w-5 ml-2" />
          </button>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / questions.length) * 100;

  return (
    <div className="max-w-3xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-500">
            {trackScore ? (
              <>Question {currentQuestionIndex + 1} of {questions.length}</>
            ) : (
              <>Word {currentQuestionIndex + 1} of {questions.length}</>
            )}
          </div>
          {trackScore && (
          <div className="text-sm text-gray-500">
            Score: {score}/{questions.length}
          </div>
          )}
        </div>

        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          ></div>
        </div>

        <div className="space-y-4">
          <p className="text-lg font-medium text-gray-900">
            {t('Choose the correct word:')}
          </p>
          {currentQuestion && (
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => playAudio(currentQuestion.sentence)}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                  title="Listen to word"
                >
                  <Volume2 className="h-5 w-5" />
                </button>
                <p className="text-xl">
                  {currentQuestion.sentence}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowTranslation(!showTranslation)}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                  title={showTranslation ? "Hide translation" : "Show translation"}
                >
                  <Eye className="h-5 w-5" />
                </button>
                {showTranslation && (
                  <p className="text-gray-600 italic">
                    {nativeLanguage === 'Persian' && currentQuestion.persian_translation
                      ? currentQuestion.persian_translation
                      : currentQuestion.translation}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {currentQuestion && (
          <div className="flex flex-wrap gap-2">
            {currentQuestion.options.map((option) => (
              <button
                key={option}
                onClick={() => handleAnswerSelect(option)}
                disabled={selectedAnswer !== null}
                className={`inline-flex items-center px-3 py-1 text-base font-medium rounded-full transition-all transform hover:scale-105 ${
                  selectedAnswer === null
                    ? 'bg-white hover:bg-indigo-50 border-2 border-indigo-300 hover:border-indigo-400 hover:shadow-md'
                    : selectedAnswer === option
                      ? isCorrect
                        ? 'bg-green-100 border-2 border-green-500 shadow-lg ring-2 ring-green-200'
                        : 'bg-red-100 border-2 border-red-500 shadow-lg ring-2 ring-red-200 line-through'
                      : option.toLowerCase().replace(/[.,!?]/g, '').trim() === 
                        currentQuestion.correctAnswer.toLowerCase().replace(/[.,!?]/g, '').trim()
                        ? 'bg-green-100 border-2 border-green-500 shadow-lg ring-2 ring-green-200'
                          : option.toLowerCase().replace(/[.,!?؟;:()'"\-]/g, '').trim() === 
                            currentQuestion.correctAnswer.toLowerCase().replace(/[.,!?؟;:()'"\-]/g, '').trim()
                            ? 'bg-green-100 border-2 border-green-500 shadow-lg ring-2 ring-green-200'
                            : 'bg-gray-50 border-2 border-gray-200 opacity-50 cursor-not-allowed'
                }`}
              >
                <span>{option}</span>
                {selectedAnswer === option && (
                  isCorrect ? (
                    <Check className="h-4 w-4 ml-2 text-green-600 animate-bounce" />
                  ) : (
                    <X className="h-4 w-4 ml-2 text-red-600" />
                  )
                )}
                {selectedAnswer !== null && 
                  option.toLowerCase().replace(/[.,!?؟;:()'"\-]/g, '').trim() === 
                  currentQuestion.correctAnswer.toLowerCase().replace(/[.,!?؟;:()'"\-]/g, '').trim() && 
                  selectedAnswer !== option && (
                    <Check className="h-4 w-4 ml-2 text-green-600 animate-bounce" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>
      {trackScore && currentQuestionIndex === questions.length - 1 && selectedAnswer && isCorrect !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-8 rounded-lg shadow-xl max-w-md w-full mx-4">
            <h2 className="text-2xl font-bold mb-4">Quiz Results</h2>
            <p className="text-lg mb-6">
              {score >= 9 ? (
                <>
                  Congratulations! You got {score} out of {questions.length} correct!
                </>
              ) : (
                <>
                  You got {score} out of {questions.length} correct. Let's try again!
                </>
              )}
            </p>
            <div className="flex justify-end">
              <button
                onClick={async () => {
                  if (score >= 9) {
                    await playSuccessSound();
                    onComplete(score);
                  } else {
                    // Reset quiz for retry
                    setCurrentQuestionIndex(0);
                    setSelectedAnswer(null);
                    setIsCorrect(null);
                    setScore(0);
                    // Shuffle questions for variety
                    setQuestions(questions => [...questions].sort(() => Math.random() - 0.5));
                  }
                }}
                className="flex items-center px-6 py-3 rounded-full bg-green-500 hover:bg-green-600 text-white transition-colors"
              >
                {score >= 9 ? 'Continue' : 'Try Again'}
                <ArrowRight className="h-5 w-5 ml-2" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}